import { useEffect } from 'react';
import { Center, Container, Grid, Loader, NavLink, Paper, Stack, Text, Title } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconBuildingSkyscraper,
  IconClock,
  IconCreditCard,
  IconHeadset,
  IconListNumbers,
  IconMicrophone,
  IconMusic,
  IconSettings,
  IconUsers,
} from '@tabler/icons-react';
import { Link, Outlet, createFileRoute, useLocation, useNavigate } from '@tanstack/react-router';
import type { LinkProps } from '@tanstack/react-router';
import { useOrg } from '@/hooks/use-org';
import { useOrgWidgets } from '@/hooks/use-org-widgets';

type SettingsItem = {
  href: NonNullable<LinkProps['to']>;
  label: string;
  description: string;
  icon: typeof IconSettings;
};

type SettingsSection = {
  label: string;
  items: SettingsItem[];
};

const organizationSection: SettingsSection = {
  label: 'Organization',
  items: [
    {
      href: '/settings/general',
      label: 'General',
      description: 'Name and workspace details',
      icon: IconBuildingSkyscraper,
    },
    { href: '/settings/members', label: 'Members', description: 'Roles, invites, and access', icon: IconUsers },
    {
      href: '/settings/recordings',
      label: 'Recordings',
      description: 'Review recorded calls',
      icon: IconMicrophone,
    },
    { href: '/settings/media', label: 'Media', description: 'Upload and manage audio media', icon: IconMusic },
    {
      href: '/settings/business-hours',
      label: 'Business Hours',
      description: 'Schedules and availability',
      icon: IconClock,
    },
    { href: '/settings/billing', label: 'Billing', description: 'Subscription and usage', icon: IconCreditCard },
  ],
};

const contactCenterSection: SettingsSection = {
  label: 'Contact Center',
  items: [
    {
      href: '/settings/queues',
      label: 'Queues',
      description: 'Routing, membership, and overflow',
      icon: IconListNumbers,
    },
    {
      href: '/settings/contact-center',
      label: 'Contact Center',
      description: 'Agent states and controls',
      icon: IconHeadset,
    },
  ],
};

const operationsSection: SettingsSection = {
  label: 'Operations',
  items: [
    {
      href: '/queue-dashboard',
      label: 'Queue Dashboard',
      description: 'Live queue activity',
      icon: IconListNumbers,
    },
  ],
};

export const Route = createFileRoute('/_authed/settings')({
  component: SettingsLayout,
});

function isActivePath(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`);
}

function SettingsLayout() {
  const navigate = useNavigate();
  const pathname = useLocation({ select: (location) => location.pathname });
  const { currentOrg, isLoading, isPlatformOperatorView } = useOrg();
  const { hasContactCenter } = useOrgWidgets();
  const isDesktop = useMediaQuery('(min-width: 75em)');

  const canManage = isPlatformOperatorView || currentOrg?.role === 'owner' || currentOrg?.role === 'admin';

  useEffect(() => {
    if (isLoading) return;
    if (!canManage) void navigate({ to: '/' });
  }, [isLoading, canManage, navigate]);

  if (isLoading || !currentOrg) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!canManage) return null;

  const sections = [organizationSection, ...(hasContactCenter ? [contactCenterSection, operationsSection] : [])];

  return (
    <Container size="xl" py="xl">
      <Grid gutter="xl">
        <Grid.Col span={{ base: 12, lg: 4, xl: 3 }}>
          <Paper
            p="md"
            radius="lg"
            withBorder
            style={isDesktop ? { position: 'sticky', top: 'calc(60px + var(--mantine-spacing-md))' } : undefined}
          >
            <Stack gap="xl">
              <div>
                <Text size="xs" fw={700} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.08em' }}>
                  Admin
                </Text>
                <Title order={3} mt="xs">
                  Organization Settings
                </Title>
                <Text size="sm" c="dimmed" mt="xs">
                  {currentOrg.name}
                </Text>
              </div>

              {sections.map((section) => (
                <Stack key={section.label} gap={4}>
                  <Text size="xs" fw={700} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.08em' }}>
                    {section.label}
                  </Text>
                  {section.items.map((item) => (
                    <NavLink
                      key={item.href}
                      component={Link}
                      to={item.href}
                      active={isActivePath(pathname, item.href)}
                      label={item.label}
                      description={item.description}
                      leftSection={<item.icon size={16} stroke={1.5} />}
                      variant="light"
                      styles={{
                        root: { borderRadius: 'var(--mantine-radius-md)' },
                        description: { lineHeight: 1.35 },
                      }}
                    />
                  ))}
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 8, xl: 9 }}>
          <Outlet />
        </Grid.Col>
      </Grid>
    </Container>
  );
}
